"use client";

import {
	ChangePasswordSchema,
	UpdateEmailSchema,
	UpdateProfileSchema,
} from "@gosaas/schemas";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	IconLock,
	IconMail,
	IconSettings,
	IconUser,
} from "@tabler/icons-react";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import type { z } from "zod";
import { MobileAppDistributionCard } from "@/components/misc/mobile-app-distribution";
import { PageHeader } from "@/components/page-header";
import { PageLayout } from "@/components/page-layout";
import { useAuth } from "@/components/providers/auth";
import { DangerZoneCard } from "@/components/settings/danger-zone";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	EmojiPicker,
	EmojiPickerContent,
	EmojiPickerSearch,
} from "@/components/ui/emoji-picker";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/input-password";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import {
	apiClient,
	requestWithHandling,
	TypedClientApiError,
} from "@/utils/api";
import { deleteAccessToken, deleteRefreshToken } from "@/utils/local-storage";

export const Route = createFileRoute("/_authorized/user/settings")({
	component: Settings,
});

export function Settings() {
	const router = useRouter();
	const { user, refresh } = useAuth();

	const userForm = useForm<z.infer<typeof UpdateProfileSchema>>({
		resolver: zodResolver(UpdateProfileSchema),
		defaultValues: {
			firstName: "",
			lastName: "",
			emoji: "",
		},
	});

	const emailForm = useForm<z.infer<typeof UpdateEmailSchema>>({
		resolver: zodResolver(UpdateEmailSchema),
		defaultValues: {
			newEmail: "",
		},
	});

	const passwordForm = useForm<z.infer<typeof ChangePasswordSchema>>({
		resolver: zodResolver(ChangePasswordSchema),
		defaultValues: {
			currentPassword: "",
			newPassword: "",
			confirmPassword: "",
		},
	});

	// Update form values when user data is available
	useEffect(() => {
		if (user) {
			userForm.reset({
				firstName: user.firstName || "",
				lastName: user.lastName || "",
				emoji: user.emoji || "",
			});
		}
	}, [user, userForm]);

	const onUserSubmit = async (data: z.infer<typeof UpdateProfileSchema>) => {
		try {
			await requestWithHandling<void>(() =>
				apiClient.api.userUpdate({
					first_name: data.firstName,
					last_name: data.lastName,
					emoji: data.emoji ? encodeURIComponent(data.emoji) : undefined,
				}),
			);
			toast.success("Profile updated successfully");
			await refresh();
		} catch (error) {
			toast.error("Profile update failed", {
				description:
					error instanceof TypedClientApiError
						? error.message
						: "An unknown error occurred.",
			});
		}
	};

	const onEmailSubmit = async (data: z.infer<typeof UpdateEmailSchema>) => {
		try {
			await requestWithHandling<void>(() =>
				apiClient.api.userEmailUpdate({
					new_email: data.newEmail,
				}),
			);
			toast.warning("Email update pending", {
				description: "Please check your email for verification.",
			});
		} catch (error) {
			toast.error("Email update failed", {
				description:
					error instanceof TypedClientApiError
						? error.message
						: "An unknown error occurred.",
			});
		}
	};

	const onPasswordSubmit = async (
		data: z.infer<typeof ChangePasswordSchema>,
	) => {
		try {
			await requestWithHandling<void>(() =>
				apiClient.api.userPasswordUpdate({
					old_password: data.currentPassword,
					new_password: data.newPassword,
				}),
			);
			toast.success("Password updated successfully", {
				description: "Please log in again to continue.",
			});
			deleteAccessToken();
			deleteRefreshToken();
			router.navigate({ to: "/auth/login" });
		} catch (error) {
			toast.error("Password update failed", {
				description:
					error instanceof TypedClientApiError
						? error.message
						: "An unknown error occurred.",
			});
		}
	};

	return (
		<PageLayout>
			<PageHeader
				breadcrumbItems={[
					{ title: "User", href: null },
					{ title: "Settings", href: "/user/settings" },
				]}
				title="Account Settings"
				description="Manage your account preferences and security"
				icon={<IconSettings className="size-6 text-primary" aria-hidden />}
			/>

			<Card className="shadow-sm">
				<CardHeader className="pb-4">
					<div className="flex items-center gap-3">
						<div className="p-2 bg-blue-500/10 rounded-lg">
							<IconUser className="h-5 w-5 text-blue-500" />
						</div>
						<div>
							<CardTitle className="text-xl">Profile Information</CardTitle>
							<p className="text-sm text-muted-foreground mt-1">
								Update your profile information and choose your avatar emoji
							</p>
						</div>
					</div>
				</CardHeader>
				<CardContent className="pt-0">
					<Form {...userForm}>
						<form
							onSubmit={userForm.handleSubmit(onUserSubmit)}
							className="space-y-6"
						>
							<FormField
								control={userForm.control}
								name="emoji"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-base font-medium">
											Avatar Emoji
										</FormLabel>
										<div className="flex items-center gap-6">
											<div className="relative">
												<div className="size-20 bg-gradient-to-br from-primary/5 to-primary/10 rounded-2xl flex items-center justify-center border-2 border-primary/20 shadow-sm">
													<span className="text-4xl">
														{field.value || "👤"}
													</span>
												</div>
											</div>
											<div className="flex flex-col gap-2">
												<Popover>
													<PopoverTrigger className="focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 rounded-lg border border-input bg-background text-sm font-medium focus-visible:ring-[3px] aria-invalid:ring-[3px] [&_svg:not([class*='size-'])]:size-4 inline-flex items-center justify-center whitespace-nowrap transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none shrink-0 [&_svg]:shrink-0 outline-none group/button select-none hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2 cursor-pointer hover:bg-primary/5 transition-colors bg-transparent">
														Choose Emoji
													</PopoverTrigger>
													<PopoverContent className="w-full p-0 h-72">
														<EmojiPicker
															onEmojiSelect={(emoji) =>
																field.onChange(emoji.emoji)
															}
														>
															<EmojiPickerSearch />
															<EmojiPickerContent />
														</EmojiPicker>
													</PopoverContent>
												</Popover>
												<p className="text-xs text-muted-foreground">
													Choose an emoji to represent your profile
												</p>
											</div>
										</div>
										<FormMessage />
									</FormItem>
								)}
							/>

							<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
								<FormField
									control={userForm.control}
									name="firstName"
									render={({ field }) => (
										<FormItem>
											<FormLabel className="text-base font-medium">
												First Name
											</FormLabel>
											<FormControl>
												<Input
													{...field}
													className="h-11 bg-background/50 border-border/50 focus:border-primary/50 transition-colors"
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={userForm.control}
									name="lastName"
									render={({ field }) => (
										<FormItem>
											<FormLabel className="text-base font-medium">
												Last Name
											</FormLabel>
											<FormControl>
												<Input
													{...field}
													className="h-11 bg-background/50 border-border/50 focus:border-primary/50 transition-colors"
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							<div className="pt-2">
								<Button
									type="submit"
									className="h-11 px-8 bg-primary hover:bg-primary/90 transition-colors"
								>
									Update Profile
								</Button>
							</div>
						</form>
					</Form>
				</CardContent>
			</Card>

			<Card className="shadow-sm">
				<CardHeader className="pb-4">
					<div className="flex items-center gap-3">
						<div className="p-2 bg-green-500/10 rounded-lg">
							<IconMail className="h-5 w-5 text-green-500" />
						</div>
						<div>
							<CardTitle className="text-xl">Email Settings</CardTitle>
							<p className="text-sm text-muted-foreground mt-1">
								Manage your email address for account notifications
							</p>
						</div>
					</div>
				</CardHeader>
				<CardContent className="pt-0">
					<Form {...emailForm}>
						<form
							onSubmit={emailForm.handleSubmit(onEmailSubmit)}
							className="space-y-6"
						>
							<div className="space-y-2">
								<FormLabel className="text-base font-medium">
									Current Email
								</FormLabel>
								<Input
									type="email"
									value={user?.email ?? ""}
									disabled
									className="h-11 bg-muted/50 text-muted-foreground border-border/30"
								/>
								<p className="text-xs text-muted-foreground">
									This is your current registered email address
								</p>
							</div>

							<FormField
								control={emailForm.control}
								name="newEmail"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-base font-medium">
											New Email Address
										</FormLabel>
										<FormControl>
											<Input
												type="email"
												{...field}
												className="h-11 bg-background/50 border-border/50 focus:border-primary/50 transition-colors"
												placeholder="Enter your new email address"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<div className="pt-2">
								<Button
									type="submit"
									className="h-11 px-8 bg-primary hover:bg-primary/90 transition-colors"
								>
									Update Email
								</Button>
							</div>
						</form>
					</Form>
				</CardContent>
			</Card>

			<Card className="mb-8 shadow-sm">
				<CardHeader className="pb-4">
					<div className="flex items-center gap-3">
						<div className="p-2 bg-orange-500/10 rounded-lg">
							<IconLock className="h-5 w-5 text-orange-500" />
						</div>
						<div>
							<CardTitle className="text-xl">Password Settings</CardTitle>
							<p className="text-sm text-muted-foreground mt-1">
								Update your password to keep your account secure
							</p>
						</div>
					</div>
				</CardHeader>
				<CardContent className="pt-0">
					<Form {...passwordForm}>
						<form
							onSubmit={passwordForm.handleSubmit(onPasswordSubmit)}
							className="space-y-6"
						>
							<FormField
								control={passwordForm.control}
								name="currentPassword"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-base font-medium">
											Current Password
										</FormLabel>
										<FormControl>
											<PasswordInput
												{...field}
												className="h-11 bg-background/50 border-border/50 focus:border-primary/50 transition-colors"
												placeholder="Enter your current password"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
								<FormField
									control={passwordForm.control}
									name="newPassword"
									render={({ field }) => (
										<FormItem>
											<FormLabel className="text-base font-medium">
												New Password
											</FormLabel>
											<FormControl>
												<PasswordInput
													{...field}
													className="h-11 bg-background/50 border-border/50 focus:border-primary/50 transition-colors"
													placeholder="Enter new password"
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={passwordForm.control}
									name="confirmPassword"
									render={({ field }) => (
										<FormItem>
											<FormLabel className="text-base font-medium">
												Confirm New Password
											</FormLabel>
											<FormControl>
												<PasswordInput
													{...field}
													className="h-11 bg-background/50 border-border/50 focus:border-primary/50 transition-colors"
													placeholder="Confirm new password"
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							<div className="pt-2">
								<Button
									type="submit"
									className="h-11 px-8 bg-primary hover:bg-primary/90 transition-colors"
								>
									Update Password
								</Button>
							</div>
						</form>
					</Form>
				</CardContent>
			</Card>

			<MobileAppDistributionCard />

			<DangerZoneCard />
		</PageLayout>
	);
}

export default Settings;
